import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { format, addDays, isSameDay, parseISO, startOfToday, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore, useAuthStore } from '../store/useStore';
import { reservationsAPI, tablesAPI, customersAPI, zonesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  CalendarDays, List, Search, ChevronLeft, ChevronRight, Plus,
  Users, Clock, Phone, Mail, MoreVertical, Check, X, UserX,
  Edit2, Trash2, RefreshCw, Eye, Loader2, LayoutGrid, AlertTriangle,
  CheckCircle2, Armchair, Timer, ChevronDown, Columns3, CalendarIcon
} from 'lucide-react';

const STATUS = {
  pending:   { label: 'Pendiente',  dot: 'bg-amber-400',   pill: 'bg-amber-50 text-amber-700 border border-amber-200',  next: ['confirmed','cancelled'] },
  confirmed: { label: 'Confirmada', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border border-emerald-200', next: ['seated','no_show','cancelled'] },
  seated:    { label: 'En mesa',    dot: 'bg-blue-500',    pill: 'bg-blue-50 text-blue-700 border border-blue-200',    next: ['completed','no_show'] },
  completed: { label: 'Completada', dot: 'bg-teal-500',    pill: 'bg-teal-50 text-teal-700 border border-teal-200',   next: [] },
  cancelled: { label: 'Cancelada',  dot: 'bg-red-400',     pill: 'bg-red-50 text-red-600 border border-red-200',      next: ['pending'] },
  no_show:   { label: 'No Show',    dot: 'bg-slate-400',   pill: 'bg-slate-100 text-slate-600 border border-slate-200',next: ['pending'] },
};

const TIMES = Array.from({ length: 29 }, (_, i) => {
  const totalMins = 600 + i * 30;
  const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
  const m = String(totalMins % 60).padStart(2, '0');
  return `${h}:${m}`;
});

const DURATIONS = [
  { value: 60,  label: '1 hora' },
  { value: 90,  label: '1h 30min' },
  { value: 120, label: '2 horas' },
  { value: 150, label: '2h 30min' },
  { value: 180, label: '3 horas' },
];

// ─── Reservation Card ─────────────────────────────────────────────────────────
function ReservationCard({ res, tables, onStatusChange, onEdit, onDelete, compact = false }) {
  const table = tables.find(t => t.id === res.table_id);
  const st = STATUS[res.status] || STATUS.pending;

  return (
    <div className={cn(
      "group bg-white rounded-xl border shadow-sm hover:shadow-md transition-all duration-200",
      compact ? "p-3" : "p-4"
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 mb-1">
            <div className={cn("w-2 h-2 rounded-full flex-shrink-0", st.dot)} />
            <p className="font-semibold text-sm truncate">{res.guest_name}</p>
            <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0", st.pill)}>
              {st.label}
            </span>
          </div>
          {/* Details */}
          <div className={cn("flex flex-wrap gap-x-3 gap-y-0.5", compact ? "text-[11px]" : "text-xs")}>
            {res.time && (
              <span className="flex items-center gap-1 text-slate-500">
                <Clock className="h-3 w-3" /> {res.time}
                {res.duration && <span className="text-slate-400">· {res.duration}min</span>}
              </span>
            )}
            {res.party_size && (
              <span className="flex items-center gap-1 text-slate-500">
                <Users className="h-3 w-3" /> {res.party_size} pers.
              </span>
            )}
            {table && (
              <span className="flex items-center gap-1 text-slate-500">
                <Armchair className="h-3 w-3" /> {table.name}
              </span>
            )}
            {res.guest_phone && !compact && (
              <span className="flex items-center gap-1 text-slate-500">
                <Phone className="h-3 w-3" /> {res.guest_phone}
              </span>
            )}
          </div>
          {res.notes && !compact && (
            <p className="mt-1.5 text-[11px] text-slate-400 italic line-clamp-1">{res.notes}</p>
          )}
        </div>

        {/* Actions menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 rounded-lg hover:bg-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreVertical className="h-4 w-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {st.next.map(s => (
              <DropdownMenuItem key={s} onClick={() => onStatusChange(res.id, s)}
                className="flex items-center gap-2 cursor-pointer">
                <div className={cn("w-2 h-2 rounded-full", STATUS[s].dot)} />
                Marcar como {STATUS[s].label}
              </DropdownMenuItem>
            ))}
            {st.next.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => onEdit(res)} className="cursor-pointer">
              <Edit2 className="h-3.5 w-3.5 mr-2" /> Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onDelete(res.id)}
              className="text-red-600 cursor-pointer focus:text-red-600">
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ─── Create/Edit Reservation Modal ────────────────────────────────────────────
function ReservationModal({ open, onClose, onSave, tables, editing = null }) {
  const [form, setForm] = useState({
    guest_name: '', guest_phone: '', guest_email: '', party_size: 2,
    date: format(new Date(), 'yyyy-MM-dd'), time: '20:00', duration: 90,
    table_id: '', notes: '', status: 'confirmed'
  });
  const [saving, setSaving] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeout = useRef(null);

  useEffect(() => {
    if (editing) {
      setForm({
        guest_name: editing.guest_name || '',
        guest_phone: editing.guest_phone || '',
        guest_email: editing.guest_email || '',
        party_size: editing.party_size || 2,
        date: editing.date || format(new Date(), 'yyyy-MM-dd'),
        time: editing.time || '20:00',
        duration: editing.duration || 90,
        table_id: editing.table_id || '',
        notes: editing.notes || '',
        status: editing.status || 'confirmed',
      });
    } else {
      setForm({
        guest_name: '', guest_phone: '', guest_email: '', party_size: 2,
        date: format(new Date(), 'yyyy-MM-dd'), time: '20:00', duration: 90,
        table_id: '', notes: '', status: 'confirmed'
      });
    }
    setCustomerQuery('');
    setCustomers([]);
  }, [editing, open]);

  const searchCustomers = useCallback(async (q) => {
    if (!q || q.length < 2) { setCustomers([]); return; }
    setSearching(true);
    try {
      const res = await customersAPI.search(q);
      setCustomers(res.data || []);
    } catch { setCustomers([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchCustomers(customerQuery), 300);
    return () => clearTimeout(searchTimeout.current);
  }, [customerQuery, searchCustomers]);

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f,
      guest_name: c.name,
      guest_phone: c.phone || f.guest_phone,
      guest_email: c.email || f.guest_email,
    }));
    setCustomers([]);
    setCustomerQuery('');
  };

  const handleSave = async () => {
    if (!form.guest_name.trim()) { toast.error('El nombre del cliente es obligatorio'); return; }
    if (!form.party_size || form.party_size < 1) { toast.error('Indica el número de personas'); return; }
    setSaving(true);
    try {
      if (editing) {
        await reservationsAPI.update(editing.id, form);
        toast.success('Reservación actualizada');
      } else {
        await reservationsAPI.create(form);
        toast.success('Reservación creada');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar la reservación');
    } finally {
      setSaving(false);
    }
  };

  const eligibleTables = tables.filter(t => t.capacity_max >= (form.party_size || 1));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-orange-500" />
            {editing ? 'Editar Reservación' : 'Nueva Reservación'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Customer search */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1 block">Buscar cliente existente</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                className="pl-8 text-sm"
                placeholder="Buscar por nombre, email o teléfono..."
                value={customerQuery}
                onChange={e => setCustomerQuery(e.target.value)}
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-slate-400" />
              )}
            </div>
            {customers.length > 0 && (
              <div className="mt-1 border rounded-lg overflow-hidden shadow-md bg-white z-50">
                {customers.slice(0, 5).map(c => (
                  <button key={c.id} onClick={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-bold flex-shrink-0">
                      {c.name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-slate-400">{c.phone} {c.email && `· ${c.email}`}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Guest info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Nombre *</Label>
              <Input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                placeholder="Nombre del cliente" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Personas *</Label>
              <Input type="number" min={1} max={50} value={form.party_size}
                onChange={e => setForm(f => ({ ...f, party_size: parseInt(e.target.value) || 1 }))}
                className="text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Teléfono</Label>
              <Input value={form.guest_phone} onChange={e => setForm(f => ({ ...f, guest_phone: e.target.value }))}
                placeholder="+52 55 0000 0000" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Email</Label>
              <Input value={form.guest_email} onChange={e => setForm(f => ({ ...f, guest_email: e.target.value }))}
                placeholder="email@ejemplo.com" className="text-sm" />
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Fecha</Label>
              <Input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Hora</Label>
              <Select value={form.time} onValueChange={v => setForm(f => ({ ...f, time: v }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Duración</Label>
              <Select value={String(form.duration)} onValueChange={v => setForm(f => ({ ...f, duration: parseInt(v) }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATIONS.map(d => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table selection */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1 block">Mesa</Label>
            <Select value={form.table_id || 'none'} onValueChange={v => setForm(f => ({ ...f, table_id: v === 'none' ? '' : v }))}>
              <SelectTrigger className="text-sm"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {eligibleTables.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} — capacidad {t.capacity_max}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status (edit only) */}
          {editing && (
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1 block">Estado</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1 block">Notas</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Alergias, ocasión especial, preferencias..." rows={2} className="text-sm resize-none" />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {editing ? 'Guardar cambios' : 'Crear reservación'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Timeline View ────────────────────────────────────────────────────────────
function TimelineView({ reservations, tables, onStatusChange, onEdit, onDelete }) {
  const HOUR_H = 64;
  const START_H = 10;
  const END_H = 24;
  const HOURS = Array.from({ length: END_H - START_H }, (_, i) => i + START_H);

  const getPos = (r) => {
    if (!r.time) return null;
    const [h, m] = r.time.split(':').map(Number);
    const top = ((h - START_H) + m / 60) * HOUR_H;
    const height = Math.max(((r.duration || 90) / 60) * HOUR_H, 30);
    return { top, height };
  };

  const statuses = ['confirmed', 'pending', 'seated', 'completed', 'cancelled', 'no_show'];
  const groups = {};
  statuses.forEach(s => { groups[s] = reservations.filter(r => r.status === s && r.time); });
  const timed = reservations.filter(r => r.time);
  const untimed = reservations.filter(r => !r.time);

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Time axis */}
      <div className="w-14 flex-shrink-0 border-r bg-slate-50 overflow-hidden">
        <div className="h-10 border-b" />
        {HOURS.map(h => (
          <div key={h} style={{ height: HOUR_H }}
            className="border-b flex items-start justify-end pr-2 pt-1">
            <span className="text-[10px] text-slate-400 font-mono">{h}:00</span>
          </div>
        ))}
      </div>

      {/* Scrollable timeline */}
      <div className="flex-1 overflow-auto relative">
        {/* Sticky header */}
        <div className="sticky top-0 z-20 bg-white border-b h-10 flex items-center px-4 gap-3">
          {Object.entries(STATUS).map(([k, v]) => {
            const count = timed.filter(r => r.status === k).length;
            if (!count) return null;
            return (
              <div key={k} className="flex items-center gap-1.5">
                <div className={cn("w-2 h-2 rounded-full", v.dot)} />
                <span className="text-xs text-slate-600">{v.label} <span className="font-semibold">{count}</span></span>
              </div>
            );
          })}
        </div>

        <div className="relative" style={{ height: HOURS.length * HOUR_H }}>
          {/* Hour grid lines */}
          {HOURS.map((h, i) => (
            <div key={h} className="absolute left-0 right-0 border-b border-slate-100"
              style={{ top: i * HOUR_H }} />
          ))}

          {/* Current time line */}
          {(() => {
            const now = new Date();
            const h = now.getHours();
            const m = now.getMinutes();
            if (h >= START_H && h < END_H) {
              const top = ((h - START_H) + m / 60) * HOUR_H;
              return (
                <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top }}>
                  <div className="relative flex items-center">
                    <div className="w-2 h-2 rounded-full bg-red-500 -ml-1 flex-shrink-0" />
                    <div className="flex-1 h-[1.5px] bg-red-400" />
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* Reservation blocks */}
          {timed.map((res, idx) => {
            const pos = getPos(res);
            if (!pos) return null;
            const st = STATUS[res.status] || STATUS.pending;
            const table = tables.find(t => t.id === res.table_id);
            // Stagger overlapping items horizontally
            const overlap = timed.slice(0, idx).filter(r2 => {
              const p2 = getPos(r2);
              if (!p2) return false;
              const aEnd = pos.top + pos.height;
              const bEnd = p2.top + p2.height;
              return pos.top < bEnd && aEnd > p2.top;
            });
            const col = overlap.length % 3;
            const width = `calc(33.33% - 8px)`;
            const left = `calc(${col * 33.33}% + 4px)`;

            return (
              <div
                key={res.id}
                className="absolute rounded-lg border-l-4 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                style={{ top: pos.top + 2, height: pos.height - 4, left, width }}
              >
                <div className={cn("h-full p-2 flex flex-col justify-between", {
                  'border-l-amber-400': res.status === 'pending',
                  'border-l-emerald-500': res.status === 'confirmed',
                  'border-l-blue-500': res.status === 'seated',
                  'border-l-teal-500': res.status === 'completed',
                  'border-l-red-400': res.status === 'cancelled',
                  'border-l-slate-400': res.status === 'no_show',
                })}>
                  <div>
                    <p className="font-semibold text-xs truncate">{res.guest_name}</p>
                    <p className="text-[10px] text-slate-500">{res.time} · {res.party_size} pers.</p>
                    {table && <p className="text-[10px] text-slate-400">{table.name}</p>}
                  </div>
                  {pos.height > 50 && (
                    <div className="flex gap-1">
                      {st.next.slice(0, 2).map(s => (
                        <button key={s} onClick={() => onStatusChange(res.id, s)}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 truncate">
                          → {STATUS[s].label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Untimed reservations */}
        {untimed.length > 0 && (
          <div className="p-4 border-t bg-slate-50">
            <p className="text-xs font-medium text-slate-500 mb-2">Sin hora asignada ({untimed.length})</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {untimed.map(res => (
                <ReservationCard key={res.id} res={res} tables={tables}
                  onStatusChange={onStatusChange} onEdit={onEdit} onDelete={onDelete} compact />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kanban View ──────────────────────────────────────────────────────────────
function KanbanView({ reservations, tables, onStatusChange, onEdit, onDelete }) {
  const columns = [
    { key: 'pending',   label: 'Pendiente',  bg: 'bg-amber-50',   border: 'border-t-amber-400' },
    { key: 'confirmed', label: 'Confirmada', bg: 'bg-emerald-50',  border: 'border-t-emerald-500' },
    { key: 'seated',    label: 'En mesa',    bg: 'bg-blue-50',    border: 'border-t-blue-500' },
    { key: 'completed', label: 'Completada', bg: 'bg-teal-50',    border: 'border-t-teal-500' },
    { key: 'cancelled', label: 'Cancelada',  bg: 'bg-red-50',     border: 'border-t-red-400' },
    { key: 'no_show',   label: 'No Show',    bg: 'bg-slate-50',   border: 'border-t-slate-400' },
  ];

  return (
    <div className="flex-1 overflow-x-auto p-4">
      <div className="flex gap-3 h-full min-w-max">
        {columns.map(col => {
          const colRes = reservations.filter(r => r.status === col.key);
          return (
            <div key={col.key} className={cn(
              "w-64 flex flex-col rounded-xl border border-t-4 shadow-sm overflow-hidden",
              col.bg, col.border
            )}>
              <div className="px-3 py-2.5 flex items-center justify-between sticky top-0 bg-inherit">
                <span className="text-sm font-semibold text-slate-700">{col.label}</span>
                <span className="text-xs bg-white rounded-full px-2 py-0.5 font-bold text-slate-600 shadow-sm">
                  {colRes.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2">
                {colRes.length === 0 ? (
                  <div className="py-8 text-center text-xs text-slate-400">Sin reservaciones</div>
                ) : colRes.map(res => (
                  <ReservationCard key={res.id} res={res} tables={tables}
                    onStatusChange={onStatusChange} onEdit={onEdit} onDelete={onDelete} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────
function ListView({ reservations, tables, onStatusChange, onEdit, onDelete }) {
  const [sort, setSort] = useState('time');

  const sorted = useMemo(() => {
    const arr = [...reservations];
    if (sort === 'time') arr.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    if (sort === 'name') arr.sort((a, b) => (a.guest_name || '').localeCompare(b.guest_name || ''));
    if (sort === 'size') arr.sort((a, b) => (b.party_size || 0) - (a.party_size || 0));
    return arr;
  }, [reservations, sort]);

  if (reservations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center py-20">
          <CalendarDays className="h-14 w-14 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Sin reservaciones para este día</p>
          <p className="text-slate-400 text-sm">Crea una nueva o navega a otra fecha</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white border-b z-10">
          <tr>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs">
              <button onClick={() => setSort('name')} className={cn(sort === 'name' && 'text-orange-500')}>Cliente</button>
            </th>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs">
              <button onClick={() => setSort('time')} className={cn(sort === 'time' && 'text-orange-500')}>Hora</button>
            </th>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs">
              <button onClick={() => setSort('size')} className={cn(sort === 'size' && 'text-orange-500')}>Personas</button>
            </th>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Mesa</th>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Contacto</th>
            <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs">Estado</th>
            <th className="px-4 py-3 w-10" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {sorted.map(res => {
            const table = tables.find(t => t.id === res.table_id);
            const st = STATUS[res.status] || STATUS.pending;
            return (
              <tr key={res.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <div>
                    <p className="font-semibold">{res.guest_name}</p>
                    {res.notes && <p className="text-xs text-slate-400 truncate max-w-[150px]">{res.notes}</p>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                  {res.time || <span className="text-slate-300">—</span>}
                  {res.duration && <span className="text-xs text-slate-400 ml-1">{res.duration}min</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{res.party_size}</span>
                </td>
                <td className="px-4 py-3 text-slate-600 hidden md:table-cell">
                  {table ? table.name : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="text-xs text-slate-500">
                    {res.guest_phone && <p>{res.guest_phone}</p>}
                    {res.guest_email && <p className="truncate max-w-[150px]">{res.guest_email}</p>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Select value={res.status} onValueChange={v => onStatusChange(res.id, v)}>
                    <SelectTrigger className="h-7 text-xs w-32 border-0 p-0 focus:ring-0">
                      <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-medium", st.pill)}>
                        {st.label}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS).map(([k, v]) => (
                        <SelectItem key={k} value={k} className="text-xs">
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", v.dot)} />
                            {v.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded hover:bg-slate-100">
                        <MoreVertical className="h-4 w-4 text-slate-400" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem onClick={() => onEdit(res)} className="cursor-pointer">
                        <Edit2 className="h-3.5 w-3.5 mr-2" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDelete(res.id)}
                        className="text-red-600 cursor-pointer focus:text-red-600">
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LibroDigital() {
  const { user } = useAuthStore();
  const { selectedDate, setSelectedDate } = useAppStore();
  
  const [view, setView] = useState('list');
  const [currentDate, setCurrentDate] = useState(
    selectedDate ? parseISO(selectedDate) : startOfToday()
  );
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const [resRes, tablesRes] = await Promise.all([
        reservationsAPI.getByDate(dateStr),
        tablesAPI.getAll(),
      ]);
      setReservations(Array.isArray(resRes.data) ? resRes.data : []);
      setTables(Array.isArray(tablesRes.data) ? tablesRes.data : []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (!user?.restaurant_id) return;
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https', 'wss').replace('http', 'ws');
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/ws/${user.restaurant_id}`);
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (['reservation_created','reservation_updated','reservation_deleted'].includes(d.type)) {
          fetchData(true);
        }
      };
    } catch {}
    return () => ws?.close();
  }, [user?.restaurant_id, fetchData]);

  const navigateDate = (dir) => {
    const nd = addDays(currentDate, dir);
    setCurrentDate(nd);
    setSelectedDate(format(nd, 'yyyy-MM-dd'));
  };

  const handleStatusChange = async (id, status) => {
    try {
      await reservationsAPI.update(id, { status });
      setReservations(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      toast.success(`Reservación marcada como ${STATUS[status]?.label}`);
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta reservación?')) return;
    try {
      await reservationsAPI.delete(id);
      setReservations(prev => prev.filter(r => r.id !== id));
      toast.success('Reservación eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const filteredReservations = useMemo(() => {
    return reservations.filter(r => {
      const q = searchTerm.toLowerCase();
      const matchSearch = !q ||
        r.guest_name?.toLowerCase().includes(q) ||
        r.guest_phone?.includes(q) ||
        r.guest_email?.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [reservations, searchTerm, statusFilter]);

  const stats = useMemo(() => ({
    total:   reservations.length,
    pending: reservations.filter(r => r.status === 'pending').length,
    confirmed: reservations.filter(r => r.status === 'confirmed').length,
    seated:  reservations.filter(r => r.status === 'seated').length,
    guests:  reservations.reduce((s, r) => s + (r.party_size || 0), 0),
  }), [reservations]);

  const views = [
    { key: 'list',     icon: List,        label: 'Lista' },
    { key: 'kanban',   icon: Columns3,    label: 'Kanban' },
    { key: 'timeline', icon: CalendarDays,label: 'Línea de tiempo' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="libro-digital-page">
      {/* Top Bar */}
      <TopBar
        title="Libro Digital"
        helpSection="libro"
        actions={
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
            onClick={() => { setEditingReservation(null); setShowCreateModal(true); }}>
            <Plus className="h-4 w-4 mr-1" /> Nueva Reservación
          </Button>
        }
      />

      {/* Date Navigator + Stats */}
      <div className="bg-white border-b px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date navigation */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="font-semibold text-slate-800 hover:bg-slate-100 gap-2">
                  <CalendarIcon className="h-4 w-4 text-orange-500" />
                  {isToday(currentDate)
                    ? 'Hoy — ' + format(currentDate, "d 'de' MMMM", { locale: es })
                    : format(currentDate, "EEEE d 'de' MMMM", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="p-0 w-auto">
                <Calendar
                  mode="single"
                  selected={currentDate}
                  onSelect={(d) => { if (d) { setCurrentDate(d); setSelectedDate(format(d, 'yyyy-MM-dd')); setShowDatePicker(false); }}}
                  locale={es}
                  className="rounded-lg"
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigateDate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            {!isToday(currentDate) && (
              <Button variant="ghost" size="xs" className="text-xs text-orange-500 hover:text-orange-600 h-7 px-2"
                onClick={() => { const t = startOfToday(); setCurrentDate(t); setSelectedDate(format(t, 'yyyy-MM-dd')); }}>
                Hoy
              </Button>
            )}
          </div>

          {/* Stats pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-sm">
              <span className="font-bold text-slate-800">{stats.total}</span>
              <span className="text-slate-400">reservaciones</span>
            </div>
            <div className="w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-slate-600">{stats.pending} pend.</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-slate-600">{stats.confirmed} conf.</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-slate-600">{stats.seated} en mesa</span>
            </div>
            <div className="w-px h-4 bg-slate-200" />
            <div className="flex items-center gap-1.5 text-sm">
              <Users className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-slate-800">{stats.guests}</span>
              <span className="text-slate-400">comensales</span>
            </div>
          </div>

          {/* Refresh */}
          <button onClick={() => fetchData(true)} disabled={refreshing}
            className="ml-auto p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Toolbar: view toggle + search + filter */}
      <div className="bg-white border-b px-4 py-2 flex flex-wrap items-center gap-3">
        {/* View toggle */}
        <div className="flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
          {views.map(v => (
            <button key={v.key} onClick={() => setView(v.key)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all",
                view === v.key ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700"
              )}>
              <v.icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{v.label}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            className="pl-8 h-8 text-sm"
            placeholder="Buscar nombre, teléfono..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Status filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 text-xs w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                <div className="flex items-center gap-2">
                  <div className={cn("w-2 h-2 rounded-full", v.dot)} />
                  {v.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filteredReservations.length !== reservations.length && (
          <span className="text-xs text-slate-500">
            {filteredReservations.length} de {reservations.length}
          </span>
        )}
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          {view === 'list' && (
            <ListView
              reservations={filteredReservations}
              tables={tables}
              onStatusChange={handleStatusChange}
              onEdit={r => { setEditingReservation(r); setShowCreateModal(true); }}
              onDelete={handleDelete}
            />
          )}
          {view === 'kanban' && (
            <KanbanView
              reservations={filteredReservations}
              tables={tables}
              onStatusChange={handleStatusChange}
              onEdit={r => { setEditingReservation(r); setShowCreateModal(true); }}
              onDelete={handleDelete}
            />
          )}
          {view === 'timeline' && (
            <TimelineView
              reservations={filteredReservations}
              tables={tables}
              onStatusChange={handleStatusChange}
              onEdit={r => { setEditingReservation(r); setShowCreateModal(true); }}
              onDelete={handleDelete}
            />
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <ReservationModal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); setEditingReservation(null); }}
        onSave={() => fetchData()}
        tables={tables}
        editing={editingReservation}
      />
    </div>
  );
}
